import { sqItemFinderStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import { isStoredTableSearch, loadStoredTableColumns } from '@/itemFinder/itemFinder.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { errorToast } from '@/utilities/toast.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { AIPayload, Search, SearchType } from '@/itemFinder/itemFinder.constants';
import type { UpdateSearchPayload } from '@/itemFinder/itemFinder.store';
import type { ItemPreviewV1 } from '@seeqdev/api/model/ItemPreviewV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

export function setName(name: string) {
  flux.dispatch('ITEM_FINDER_SET_NAME', { name });
}

export function setCronSchedule(cronSchedule: string[]) {
  flux.dispatch('ITEM_FINDER_SET_CRON_SCHEDULE', { cronSchedule });
}

export function setTimezone(timezone: string | undefined) {
  flux.dispatch('ITEM_FINDER_SET_TIMEZONE', { timezone });
}

export function addSearch() {
  flux.dispatch('ITEM_FINDER_ADD_SEARCH');
}

export function removeSearch(index: number) {
  flux.dispatch('ITEM_FINDER_REMOVE_SEARCH', index);
}

export function updateSearch(payload: UpdateSearchPayload) {
  flux.dispatch('ITEM_FINDER_UPDATE_SEARCH', payload);
}

export function setStoredTablesAvailable(storedTablesAvailable: ItemPreviewV1[]) {
  flux.dispatch('ITEM_FINDER_SET_STORED_TABLES_AVAILABLE', { storedTablesAvailable });
}

export function clearInvalidStoredTables(storedTables: ItemSearchPreviewV1[]) {
  const searches = sqItemFinderStore.searches;
  const storedTableIds = storedTables.map((st) => st.id);
  searches
    .filter((search) => isStoredTableSearch(search) && !storedTableIds.includes(search.storedTableId))
    .forEach((search) => {
      updateSearch({
        searchId: search.searchId,
        action: 'UPDATE_SEARCH_FIELDS',
        searchData: {
          storedTableId: undefined,
          storedTableColumns: [],
        },
      });
    });
}

export async function refreshStoredTables() {
  try {
    const { data } = await sqItemsApi.searchItems({
      types: [SeeqNames.Types.StoredTable],
      scope: [sqWorkbookStore.workbookId],
      limit: 1000,
    });
    setStoredTablesAvailable(data.items ?? []);
    clearInvalidStoredTables(data.items ?? []);
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export async function updateStoredTable(storedTableId: string) {
  const searchesToUpdate = sqItemFinderStore.searches.filter(
    (search) => isStoredTableSearch(search) && search.storedTableId === storedTableId,
  );

  if (searchesToUpdate.length === 0) {
    return;
  }

  const storedTableColumns = await loadStoredTableColumns(storedTableId);

  searchesToUpdate.forEach((search) => {
    updateSearch({
      searchId: search.searchId,
      action: 'UPDATE_SEARCH_FIELDS',
      searchData: {
        storedTableId,
        storedTableColumns,
      },
    });
  });
}

export function resetItemFinder() {
  flux.dispatch('RESET_ITEM_FINDER', {});
}

export function setItemFinder({
  value: { id, name, searches, cronSchedule, timezone, isVantage },
}: {
  value: {
    id: string;
    name: string;
    searches: Search[] | undefined;
    cronSchedule: string[] | undefined;
    timezone?: string;
    isVantage: boolean;
  };
}) {
  flux.dispatch('SET_EXISTING_ITEM_FINDER', { value: { id, name, searches, cronSchedule, timezone, isVantage } });
}

export function setPotentialSearchesPayload(payload: AIPayload) {
  if (payload.propertySearches) {
    payload.propertySearches.forEach((propertySearch) => {
      flux.dispatch('ITEM_FINDER_ADD_PROPERTY_SEARCH', propertySearch);
    });
  }
  if (payload.fixedListSearches) {
    payload.fixedListSearches.forEach((propertySearch) => {
      flux.dispatch('ITEM_FINDER_ADD_FIXED_LIST_SEARCH', propertySearch);
    });
  }
}

export function reorderSearches(fromIndex: number, toIndex: number) {
  flux.dispatch('ITEM_FINDER_REORDER_SEARCHES', { fromIndex, toIndex });
}

export function changeSearchType(searchType: SearchType, search: Search) {
  const searchId = search.searchId;
  updateSearch({
    searchId,
    action: 'UPDATE_SEARCH_FIELDS',
    searchData: {
      searchType,
      folderId: undefined,
      types: [], // Reset to ensure conditions aren't shown if switching from folder to property
      ancestorId: undefined,
      storedTableId: undefined,
      storedTableColumns: [],
      isSimpleSearch: undefined,
      hierarchyId: undefined,
      hierarchyTableProperties: [],
      hierarchyLevelColumns: [],
      level: undefined,
      hierarchySearchMode: undefined,
      hierarchyPaths: [],
      searchItemTable: undefined,
      searchHierarchy: undefined,
    },
  });

  search.predicates?.forEach((property: { propertyId: string }) => {
    updateSearch({
      searchId,
      propertyId: property.propertyId,
      action: 'REMOVE_PROPERTY',
    });
  });

  search.ancestorPredicates?.forEach((ancestorPredicate) => {
    updateSearch({
      searchId,
      ancestorPredicateId: ancestorPredicate.ancestorPredicateId,
      action: 'REMOVE_ANCESTOR_PREDICATE',
    });
  });

  if (
    searchType === SearchTypeEnum.WORKBENCH ||
    searchType === SearchTypeEnum.PROPERTY ||
    searchType === SearchTypeEnum.HIERARCHY
  ) {
    updateSearch({
      searchId,
      action: 'ADD_PROPERTY',
    });
  }

  if (searchType === SearchTypeEnum.STOREDTABLE) {
    updateSearch({
      searchId,
      action: 'ADD_DEFAULT_STORED_TABLE_SEARCH',
    });
  }
}
